import React, { useRef, useState, useEffect, useMemo } from 'react';
import {
    Upload,
    Users,
    BookOpen,
    GraduationCap,
    Play,
    Download,
    CheckCircle,
    Clock,
    AlertCircle,
    Unlock,
    FileText,
    ExternalLink,
    FileSpreadsheet,
    CheckCircle2,
    Settings,
    RefreshCcw,
    Calculator
} from 'lucide-react';
import { useAssessment, DEFAULT_LATE_POLICY } from './SharedContext';
import { parseExcelFile, validateStudentData } from '../utils/excelParser';
import { syncRosterAcrossSessionAndGradebook } from '../utils/rosterSync';
import { getSchoolLogo, generateReportHeader, getReportStyles, hasSchoolLogo } from './logoIntegrationUtility';
import { calculateLevelPoints, calculateRubricFinalScore } from '../utils/rubricCalculations';

// Add new imports for grading policy management
import gradingPolicyService from '../services/gradingPolicyService';
import {
    useGradingPolicies,
    useGradingPolicyForProgram,
    useGradeCalculation,
    useGradingPolicyManager
} from '../hooks/useGradingPolicies';
// Grade calculation – single source of truth
import { computeGrade } from '../utils/computeGrade';
import { ProgramTypes } from '../models/gradingPolicy';
/**
 * getLetterGrade – uses the single-source-of-truth computeGrade().
 * Kept async for backward-compatibility with existing callers.
 */
const getLetterGrade = async (percentage, programType) => {
    return computeGrade({ programType, percent: percentage }).letter;
};

const parseHexColor = (color) => {
    if (!color || typeof color !== 'string') return null;

    const hex = color.replace('#', '');
    if (hex.length !== 3 && hex.length !== 6) return null;

    const fullHex = hex.length === 3
        ? hex.split('').map(char => char + char).join('')
        : hex;

    const r = parseInt(fullHex.slice(0, 2), 16);
    const g = parseInt(fullHex.slice(2, 4), 16);
    const b = parseInt(fullHex.slice(4, 6), 16);

    if ([r, g, b].some(value => Number.isNaN(value))) return null;

    return { r, g, b };
};

const getContrastingTextColor = (backgroundColor) => {
    const rgb = parseHexColor(backgroundColor);

    if (!rgb) {
        return '#111827';
    }

    const brightness = ((rgb.r * 299) + (rgb.g * 587) + (rgb.b * 114)) / 1000;
    return brightness > 160 ? '#111827' : '#ffffff';
};

const getLevelBadgeColors = (color) => {
    const background = color || '#6b7280';
    const text = getContrastingTextColor(background);

    return { background, text };
};

const ClassListManager = () => {
    const fileInputRef = useRef(null);
    const reloadFileInputRef = useRef(null);
    const [importStatus, setImportStatus] = useState('');
    const [isDragActive, setIsDragActive] = useState(false);
    const [showResetConfirm, setShowResetConfirm] = useState(false);
    const [reloadResult, setReloadResult] = useState(null);
    const [isReloading, setIsReloading] = useState(false);

    // Add new grading policy state management using TanStack Query hooks
    const { data: availablePolicies = [], isLoading: policiesLoading } = useGradingPolicies({ isActive: true });
    const [currentProgramType, setCurrentProgramType] = useState(
        () => gradingPolicyService.getCurrentProgramTypeSelection() || 'Degree'
    );
    const { data: selectedPolicy } = useGradingPolicyForProgram(currentProgramType);
    const gradeCalculation = useGradeCalculation();
    const { prefetchPolicyForProgram } = useGradingPolicyManager();

    const {
        classList,
        setClassList,
        gradingSession,
        setGradingSession,
        initializeGradingSession,
        setActiveTab,
        setCurrentStudent,
        hasDraft,
        getGradeStatus,
        saveFinalGrade,
        loadDraft,
        saveDraft,
        drafts,
        sharedRubric,
        updateStudentInfo,
        updateAssignmentInfo,
        updateCourseInfo, // FIXED: Added updateCourseInfo from the context
        loadFinalGrade,
        finalGrades,
        rubricFormData,
        calculateScoreWithLatePolicy,
        getEffectiveLatePolicy,
        resetGradingState,
        gradeBook,
        setGradeBook,
        gradingData,
    } = useAssessment();

    const getSafeLatePolicy = (courseId) => getEffectiveLatePolicy(courseId) || DEFAULT_LATE_POLICY;

    const hasStudents = classList?.students?.length > 0;

    

    const normalizeProgramTypeValue = (value) => gradingPolicyService.normalizeProgramType(value) || 'Degree';

    useEffect(() => {
        if (availablePolicies.length === 0) return;

        const storedSelection = gradingPolicyService.getCurrentProgramTypeSelection();
        if (storedSelection) {
            setCurrentProgramType(normalizeProgramTypeValue(storedSelection));
            return;
        }

        const defaults = gradingPolicyService.getProgramPolicyDefaults();
        const preferredProgram = Object.keys(defaults)[0];
        if (preferredProgram) {
            setCurrentProgramType(normalizeProgramTypeValue(preferredProgram));
        }
    }, [availablePolicies]);

    // Load current program type from class metadata and update policy
    useEffect(() => {
        if (classList?.courseMetadata?.programType) {
            setCurrentProgramType(normalizeProgramTypeValue(classList.courseMetadata.programType));
        }
    }, [classList?.courseMetadata?.programType]);

    // Prefetch policies for better UX
    useEffect(() => {
        ProgramTypes.forEach(type => {
            const normalizedType = normalizeProgramTypeValue(type);
            if (normalizedType !== currentProgramType) {
                prefetchPolicyForProgram(normalizedType);
            }
        });
    }, [currentProgramType, prefetchPolicyForProgram]);

    // Enhanced handler to change the program type for grading with policy loading
    const handleProgramTypeChange = (e) => {
        const newType = normalizeProgramTypeValue(e.target.value);
        setCurrentProgramType(newType);
        gradingPolicyService.setCurrentProgramTypeSelection(newType);
        setClassList(prev => {
            if (!prev) return prev;

            return {
                ...prev,
                courseMetadata: {
                    ...prev.courseMetadata,
                    programType: newType,
                },
            };
        });
    };

    // Helper function for status display (preserved exactly from original)
    const getStatusDisplay = (progress, studentId) => {
        const gradeStatus = getGradeStatus(studentId);

        if (gradeStatus === 'final') {
            return (
                <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full font-medium">
                        Final
                    </span>
                </div>
            );
        } else if (gradeStatus === 'draft') {
            return (
                <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-yellow-600" />
                    <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full font-medium">
                        Draft
                    </span>
                </div>
            );
        } else if (progress?.status === 'in-progress') {
            return (
                <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                    <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full font-medium">
                        In Progress
                    </span>
                </div>
            );
        } else {
            return (
                <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-gray-300 rounded-full"></div>
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                        Not Started
                    </span>
                </div>
            );
        }
    };

    // Helper function to finalize a draft grade (preserved exactly from original)
    const finalizeGrade = (studentId) => {
        console.log('🔧 Finalizing grade for student:', studentId);

        const draftData = drafts[studentId];
        if (draftData) {
            // Save the final grade
            saveFinalGrade(studentId, draftData);

            // Update class list progress with better logging
            const studentIndex = classList.students.findIndex(s => s.id === studentId);
            console.log('📍 Found student at index:', studentIndex);

            if (studentIndex >= 0) {
                const updatedProgress = [...classList.gradingProgress];
                const previousProgress = updatedProgress[studentIndex];

                updatedProgress[studentIndex] = {
                    ...updatedProgress[studentIndex],
                    status: 'completed_final',
                    lastModified: new Date().toISOString(),
                    gradeType: 'final'
                };

                console.log('📊 Progress update:', {
                    studentId,
                    before: previousProgress,
                    after: updatedProgress[studentIndex]
                });

                setClassList(prev => ({
                    ...prev,
                    gradingProgress: updatedProgress
                }));

                console.log('✅ Class list updated successfully');
            } else {
                console.error('❌ Student not found in class list:', studentId);
            }
        } else {
            console.error('❌ No draft data found for student:', studentId);
        }
    };

    // Helper function to unlock a final grade (preserved exactly from original)
    const unlockGrade = (studentId) => {
        // For now, we'll implement this by updating the class list progress
        // The actual unlocking logic should be implemented in SharedContext
        const studentIndex = classList.students.findIndex(s => s.id === studentId);
        if (studentIndex >= 0) {
            const updatedProgress = [...classList.gradingProgress];
            updatedProgress[studentIndex] = {
                ...updatedProgress[studentIndex],
                status: 'completed_draft',
                lastModified: new Date().toISOString(),
                gradeType: 'draft'
            };
            setClassList(prev => ({
                ...prev,
                gradingProgress: updatedProgress
            }));
        }

        // Note: Full unlock functionality requires additional implementation in SharedContext
        console.log('Unlock grade for student:', studentId);
    };

    // Helper function to load final grade data (preserved exactly from original)
    const loadFinalGradeData = (studentId) => {
        // Use loadFinalGrade if available, otherwise fallback to checking finalGrades directly
        if (typeof loadFinalGrade === 'function') {
            return loadFinalGrade(studentId);
        }
        // Fallback: check finalGrades directly if available
        if (finalGrades && finalGrades[studentId]) {
            return finalGrades[studentId];
        }
        // Last resort: check drafts (this maintains current behavior)
        return drafts[studentId] || null;
    };

    // **ENHANCED**: This function now uses dynamic grading with fallback to legacy calculation
    // ENHANCED calculateStudentGrade function for ClassListManager.js
    // Replace the existing calculateStudentGrade function (around lines 200-280)

    const calculateStudentGrade = async (studentId) => {
        const gradeStatus = getGradeStatus(studentId);
        let gradeData = null;

        if (gradeStatus === 'final') {
            gradeData = loadFinalGradeData(studentId);
        } else if (gradeStatus === 'draft') {
            gradeData = loadDraft(studentId);
        }

        // If no grade data, return empty result
        if (!gradeData || !sharedRubric || !sharedRubric.criteria) {
            return {
                score: 'N/A',
                maxPossible: sharedRubric?.assignmentInfo?.totalPoints || 100,
                percentage: 'N/A',
                letterGrade: 'N/A'
            };
        }

        const rubricTotalPoints = sharedRubric.assignmentInfo?.totalPoints || gradeData.assignment?.maxPoints || 100;

        const rubricScore = calculateRubricFinalScore({
            criteria: sharedRubric.criteria,
            rubricLevels: sharedRubric.rubricLevels,
            rubricGrading: gradeData.rubricGrading,
            rubricTotalPoints
        });

        const totalScore = rubricScore.finalPoints;
        const maxScore = rubricTotalPoints;

        // Apply late policy if applicable
        const penaltyResult = calculateScoreWithLatePolicy(totalScore, {
            dueAt: gradeData.assignment?.dueAt || gradeData.assignment?.dueDate || sharedRubric.assignmentInfo?.dueDate,
            submittedAt: gradeData.metadata?.submittedAt,
            hasApprovedExtension: gradeData.metadata?.hasApprovedExtension,
        }, gradeData.course?.code);
        let finalScore = penaltyResult.finalScore;

        // Calculate percentage
        const percentage = maxScore > 0 ? Math.round((finalScore / maxScore) * 100) : 0;

        // ENHANCED: Determine program type from student and class data
        const student = classList.students.find(s => s.id === studentId);
        const programType = normalizeProgramTypeValue(student?.program || classList.courseMetadata?.programType || currentProgramType);
        // Use the single-source-of-truth computeGrade() for letter grade
        const letterGrade = computeGrade({ programType, percent: percentage }).letter;

        return {
            score: Math.round(finalScore * 10) / 10,
            maxPossible: maxScore,
            percentage,
            letterGrade
        };
    };

    // Helper function to load student for grading (preserved exactly from original)
    const loadStudentForGrading = (student) => {
        const gradeStatus = getGradeStatus(student.id);

        // Sync session index so header position and next/previous navigation stay correct
        setGradingSession((prev) => {
            if (!prev?.classListData?.students?.length) return prev;
            const matchedIndex = prev.classListData.students.findIndex((item) => item?.id === student?.id);
            if (matchedIndex < 0) return prev;

            return {
                ...prev,
                currentStudentIndex: matchedIndex,
                currentStudent: prev.classListData.students[matchedIndex]
            };
        });

        // Set the current student first
        setCurrentStudent(student);

        // Load the student info into the grading form
        updateStudentInfo('name', student.name || '');
        updateStudentInfo('id', student.id || '');
        updateStudentInfo('email', student.email || '');

        // Load rubric assignment info if available
        if (sharedRubric) {
            updateAssignmentInfo({
                name: sharedRubric.assignmentInfo?.title || '',
                maxPoints: sharedRubric.assignmentInfo?.totalPoints || 100
            });
        }

        // Provide user feedback and load appropriate data
        if (gradeStatus === 'final') {
            const finalData = loadFinalGradeData(student.id);
            console.log(`Loading final grade for ${student.name}:`, finalData ? 'Found' : 'Not found');
        } else if (gradeStatus === 'draft') {
            const draftData = loadDraft(student.id);
            console.log(`Loading draft for ${student.name}:`, draftData ? 'Found' : 'Not found');
        } else {
            console.log(`Starting new grade for ${student.name}`);
        }

        // Switch to grading tool - the GradingTemplate useEffect will handle loading the data
        setActiveTab('grading-tool');

        // Note: The actual data loading happens in GradingTemplate's useEffect for currentStudent
    };


    // Helper function to ensure grading progress is synchronized
    const syncGradingProgress = () => {
        if (!classList) return;

        const updatedProgress = classList.gradingProgress.map((progress, index) => {
            const student = classList.students[index];
            const gradeStatus = getGradeStatus(student.id);

            // Sync the progress with actual grade status
            const syncedProgress = { ...progress };

            if (gradeStatus === 'final') {
                if (syncedProgress.gradeType !== 'final' || syncedProgress.status !== 'completed_final') {
                    syncedProgress.gradeType = 'final';
                    syncedProgress.status = 'completed_final';
                }
                if (!syncedProgress.lastModified) {
                    syncedProgress.lastModified = new Date().toISOString();
                }
            } else if (gradeStatus === 'draft') {
                if (syncedProgress.gradeType !== 'draft' || syncedProgress.status !== 'completed_draft') {
                    syncedProgress.gradeType = 'draft';
                    syncedProgress.status = 'completed_draft';
                }
                if (!syncedProgress.lastModified) {
                    syncedProgress.lastModified = new Date().toISOString();
                }
            }

            return syncedProgress;
        });

        // Update the class list if changes were made
        const hasChanges = updatedProgress.some((progress, index) =>
            JSON.stringify(progress) !== JSON.stringify(classList.gradingProgress[index])
        );

        if (hasChanges) {
            console.log('🔄 Syncing grading progress with actual grade status');
            setClassList(prev => ({
                ...prev,
                gradingProgress: updatedProgress
            }));
        }
    };

    useEffect(() => {
        if (!classList) return;
        syncGradingProgress();
    }, [drafts, finalGrades, classList]);

    const resetGradingStatuses = () => {
        resetGradingState();
    };

    // Helper function to get grading progress statistics (preserved exactly from original)
    const getGradingProgress = () => {
        if (!classList) return { completed: 0, total: 0, percentage: 0, final: 0, draft: 0 };

        let final = 0;
        let draft = 0;

        // Use actual grade status instead of stored progress data
        classList.students.forEach((student, index) => {
            const progress = classList.gradingProgress?.[index];

            if (progress?.status === 'not_started') {
                return;
            }
            const gradeStatus = getGradeStatus(student.id);

            if (gradeStatus === 'final') {
                final++;
            } else if (gradeStatus === 'draft') {
                draft++;
            }
        });

        const completed = final + draft;
        const total = classList.students.length;
        const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;

        return { completed, total, percentage, final, draft };
    };

    // FIXED: Export class grades as CSV with async/await properly handled
    const exportClassGradesCSV = async () => {
        if (!classList) return;

        // Ensure grading progress is synchronized before export
        syncGradingProgress();
        await new Promise(resolve => setTimeout(resolve, 100)); // Brief pause for state update

        console.log('🔄 Starting CSV export with async grade calculations...');

        // 1) Calculate grades for all students asynchronously
        const studentGrades = await Promise.all(
            classList.students.map(async (student, index) => {
                const progress = classList.gradingProgress[index] || {};
                const gradeStatus = getGradeStatus(student.id); // Use consistent status check
                const gradeInfo = await calculateStudentGrade(student.id);

                // Ensure gradeType is consistent with status
                const actualGradeType = gradeStatus === 'final' ? 'final' :
                    gradeStatus === 'draft' ? 'draft' :
                        progress.gradeType || 'N/A';

                const actualStatus = gradeStatus === 'final' ? 'completed_final' :
                    gradeStatus === 'draft' ? 'completed_draft' :
                        progress.status || 'pending';

                const lm = progress.lastModified
                    ? new Date(progress.lastModified).toLocaleDateString()
                    : 'Never';

                return [
                    index + 1,                            // # column
                    student.id,
                    student.name,
                    student.email,
                    student.program || 'N/A',
                    actualStatus,
                    actualGradeType,
                    gradeInfo.score !== 'N/A'
                        ? `${gradeInfo.score}/${gradeInfo.maxPossible}`
                        : 'N/A',
                    gradeInfo.letterGrade || 'N/A',
                    gradeInfo.percentage !== 'N/A'
                        ? `${gradeInfo.percentage}%`
                        : 'N/A',
                    lm
                ];
            })
        );

        // 2) Build the complete data array with headers
        const rows = [
            ['#', 'Student ID', 'Student Name', 'Email', 'Program', 'Status', 'Grade Type', 'Numeric Grade', 'Letter Grade', 'Percentage', 'Last Modified'],
            ...studentGrades
        ];

        // 3) Quote each cell and create CSV
        const quote = cell => `"${String(cell).trim().replace(/"/g, '""')}"`;
        const csvContent = rows
            .map(row => row.map(quote).join(','))
            .join('\r\n');

        // 4) Trigger download
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${classList.courseMetadata?.courseCode || 'class'}_grades_${new Date().toISOString().slice(0, 10)}.csv`;
        link.click();
        URL.revokeObjectURL(url);

        console.log('✅ CSV export completed with letter grades');
    };

    // Enhanced version of your existing getClassGradesHTML function
    const getClassGradesHTMLWithLogo = async () => {
        const now = new Date();
        const currentDate = now.toLocaleDateString('en-CA');
        const currentTime = now.toLocaleTimeString('en-CA');

        console.log('🔄 Generating class grades HTML with logo...');

        // Calculate grades for all students asynchronously (your existing logic)
        // Calculate grades for all students asynchronously (your existing logic)
        const studentRows = await Promise.all(
            classList.students.map(async (student, idx) => {
                const progress = classList.gradingProgress[idx] || {};
                const gradeStatus = getGradeStatus(student.id);
                const info = await calculateStudentGrade(student.id);

                // Ensure consistent gradeType and status
                const actualGradeType = gradeStatus === 'final' ? 'final' :
                    gradeStatus === 'draft' ? 'draft' :
                        progress.gradeType || 'N/A';

                const actualStatus = gradeStatus === 'final' ? 'completed_final' :
                    gradeStatus === 'draft' ? 'completed_draft' :
                        progress.status || 'pending';

                const gradeDisplay = info.score !== 'N/A' ? `${info.score}%` : 'N/A';
                const letterGrade = info.letterGrade || 'N/A';
                const percentage = info.score !== 'N/A' ? `${info.score}%` : 'N/A';
                const lastModified = progress.lastModified
                    ? new Date(progress.lastModified).toLocaleDateString('en-CA')
                    : 'Not graded';

                return `
        <tr>
          <td>${idx + 1}</td>
          <td>${student.id}</td>
          <td>${student.name}</td>
          <td>${student.email}</td>
          <td>${student.program}</td>
          <td>${actualStatus}</td>
          <td>${actualGradeType}</td>
          <td>${gradeDisplay}</td>
          <td>${letterGrade}</td>
          <td>${percentage}</td>
          <td>${lastModified}</td>
        </tr>
      `;
            })
        );

        const rowsHtml = studentRows.join('');

        // Use the logo utility to generate header
        const reportData = {
            title: '📊 Class Grade Report',  // ← FIXED: Correct title for class report
            courseCode: classList.courseMetadata?.courseCode || '',
            courseName: classList.courseMetadata?.courseName || '',
            section: classList.courseMetadata?.section || '',
            rubricTitle: sharedRubric?.assignmentInfo?.title || 'Unnamed Rubric'
        };

        return `<!DOCTYPE html>
<html>
<head>
  <title>Class Grade Report</title>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  ${getReportStyles()}
</head>
<body>
  ${generateReportHeader(reportData)}

  <div class="table-container">
    <table>
      <thead>
        <tr>
          <th>#</th><th>Student ID</th><th>Name</th><th>Email</th>
          <th>Program</th><th>Status</th><th>Grade Type</th>
          <th>Final Grade</th><th>Letter Grade</th><th>Percentage</th><th>Last Modified</th>
        </tr>
      </thead>
      <tbody>
        ${rowsHtml}
      </tbody>
    </table>
  </div>

  <div class="footer">
    Imported from: ${classList.fileName}
  </div>
</body>
</html>`;
    };

    // FIXED: Export class grades as HTML with async/await properly handled
    const exportClassGradesHTML = async () => {
        if (!classList) return;

        // Ensure grading progress is synchronized before export
        syncGradingProgress();
        await new Promise(resolve => setTimeout(resolve, 100)); // Brief pause for state update

        console.log('🔄 Exporting HTML with async grade calculations...');

        // Use the logo-enhanced version
        const htmlContent = await getClassGradesHTMLWithLogo();
        const blob = new Blob([htmlContent], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');

        link.href = url;
        link.download = `${classList.courseMetadata?.courseCode || 'Class'}_grades.html`;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(url);

        console.log('✅ HTML export completed with letter grades and logo');
    };

    // Enhanced version of your existing getPortraitClassReportHTML function
    const getPortraitClassReportHTMLWithLogo = async () => {
        const now = new Date();
        const currentDate = now.toLocaleDateString('en-CA');
        const currentTime = now.toLocaleTimeString('en-CA');
        const rubricName = sharedRubric?.assignmentInfo?.title || 'Unnamed Rubric';

        console.log('🔄 Generating portrait report with logo and async grade calculations...');

        // Calculate grades for all students asynchronously (FIXED: Added Promise.all and stored result)
        const studentRows = await Promise.all(
            classList.students.map(async (student, idx) => {
                const progress = classList.gradingProgress[idx] || {};
                const gradeStatus = getGradeStatus(student.id);
                const info = await calculateStudentGrade(student.id);

                // Ensure status consistency
                const actualStatus = gradeStatus === 'final' ? 'completed_final' :
                    gradeStatus === 'draft' ? 'completed_draft' :
                        progress.status || 'pending';

                const num = info.score !== 'N/A'
                    ? parseFloat(info.score)
                    : null;

                const rowClass = num !== null
                    ? (num >= 80 ? 'high-grade' : num >= 60 ? 'medium-grade' : 'low-grade')
                    : 'no-grade';

                return `
        <tr class="${rowClass}">
          <td style="text-align:center">${idx + 1}</td>
          <td>${student.id}</td>
          <td><strong>${student.name}</strong></td>
          <td>${student.email}</td>
          <td>${student.program}</td>
          <td style="text-align:center">${info.score !== 'N/A' ? `${info.score}%` : 'N/A'}</td>
          <td style="text-align:center; font-weight:bold">${info.letterGrade || 'N/A'}</td>
        </tr>
      `;
            })
        );

        const studentRowsHtml = studentRows.join('');

        // Use the logo utility to generate header
        const reportData = {
            title: '📊 Class Grade Report (Portrait)',
            courseCode: classList.courseMetadata?.courseCode || '',
            courseName: classList.courseMetadata?.courseName || '',
            section: classList.courseMetadata?.section || '',
            rubricTitle: rubricName
        };

        return `<!DOCTYPE html>
<html>
<head>
  <title>Class Grade Report</title>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  ${getReportStyles()}
  <style>
    .high-grade { background-color: #d4edda !important; }
    .medium-grade { background-color: #fff3cd !important; }
    .low-grade { background-color: #f8d7da !important; }
    .no-grade { background-color: #f8f9fa !important; }
    
    @page {
      size: A4 portrait;
      margin: 15mm;
    }
    
    @media print {
      body { 
        font-size: 10pt; 
        background: white;
      }
      .school-logo, .report-logo { 
        max-height: 50px !important; 
      }
      table { 
        page-break-inside: auto; 
      }
      tr { 
        page-break-inside: avoid; 
        page-break-after: auto; 
      }
      thead { 
        display: table-header-group; 
      }
    }
  </style>
</head>
<body>
  ${generateReportHeader(reportData, { maxHeight: 60 })}

  <div class="table-container">
    <table>
      <thead>
        <tr style="background-color: #2563eb; color: white;">
          <th style="text-align:center">#</th>
          <th>Student ID</th>
          <th>Name</th>
          <th>Email</th>
          <th>Program</th>
          <th style="text-align:center">Grade</th>
          <th style="text-align:center">Letter</th>
        </tr>
      </thead>
      <tbody>
        ${studentRowsHtml}
      </tbody>
    </table>
  </div>

  <div class="footer">
    <div style="margin-top: 20px; text-align: center; font-size: 12px; color: #666;">
      <strong>Grade Distribution Summary</strong><br>
      Report generated on ${currentDate} at ${currentTime}<br>
      Total Students: ${classList.students.length} | Rubric: ${rubricName}
    </div>
  </div>
</body>
</html>`;
    };

    // FIXED: Export portrait PDF with async/await properly handled
    const exportClassGradesPortraitPDF = async () => {
        if (!classList) return;

        // Ensure grading progress is synchronized before export
        syncGradingProgress();
        await new Promise(resolve => setTimeout(resolve, 100)); // Brief pause for state update

        console.log('🔄 Generating PDF with async grade calculations and logo...');

        // Use the logo-enhanced version
        const htmlContent = await getPortraitClassReportHTMLWithLogo();
        const printWin = window.open('', '_blank', 'width=800,height=600');
        printWin.document.write(htmlContent);
        printWin.document.close();
        printWin.focus();

        printWin.onload = () => {
            printWin.print();
        };

        console.log('✅ PDF generation completed with letter grades and logo');
    };

    // Complete exportStudentGrade function for ClassListManager.js (preserved exactly from original)
    const exportStudentGrade = (studentId) => {
        // 1. Make sure we have a classList and sharedRubric
        if (!classList || !sharedRubric) {
            alert('No class list or rubric available. Please ensure both are loaded.');
            return;
        }

        // 2. Find the student in the class list
        const student = classList.students.find(s => s.id === studentId);
        if (!student) {
            alert('Student not found in class list.');
            return;
        }

        // 3. Load the student's grade data
        const gradeStatus = getGradeStatus(studentId);
        let gradeData = null;

        if (gradeStatus === 'final') {
            gradeData = loadFinalGradeData(studentId);
        } else if (gradeStatus === 'draft') {
            gradeData = loadDraft(studentId);
        } else {
            alert('No grade data found for this student. Please grade the student first.');
            return;
        }

        if (!gradeData) {
            alert('Unable to load grade data for this student.');
            return;
        }

        // 5. Calculate scores (replicated from GradingTemplate.js)
        const calculateTotalScore = () => {
            if (!sharedRubric || !gradeData.rubricGrading) {
                return { finalScore: 0, rawScore: 0, penaltyApplied: false };
            }

            const rubricTotalPoints = sharedRubric.assignmentInfo.totalPoints || gradeData.assignment?.maxPoints || 100;

            const rubricScore = calculateRubricFinalScore({
                criteria: sharedRubric.criteria,
                rubricLevels: sharedRubric.rubricLevels,
                rubricGrading: gradeData.rubricGrading,
                rubricTotalPoints
            });

            const rawScore = rubricScore.finalPoints;
            const penaltyResult = calculateScoreWithLatePolicy(rubricScore.finalPoints, {
                dueAt: gradeData.assignment?.dueAt || gradeData.assignment?.dueDate || sharedRubric.assignmentInfo?.dueDate,
                submittedAt: gradeData.metadata?.submittedAt,
                hasApprovedExtension: gradeData.metadata?.hasApprovedExtension,
            }, gradeData.course?.code);

            return {
                finalScore: penaltyResult.finalScore,
                rawScore,
                penaltyApplied: penaltyResult.penaltyPercentage > 0
            };
        };

        // 6. Generate HTML report (based on GradingTemplate.js generateStudentReportHTML)
        // 6. Generate HTML report with logo integration (UPDATED VERSION)
        const generateStudentReportHTML = () => {
            const scoreCalculation = calculateTotalScore();
            const totalScore = scoreCalculation.finalScore;
            const rawScore = scoreCalculation.rawScore;
            const maxPoints = sharedRubric.assignmentInfo.totalPoints || gradeData.assignment?.maxPoints || 100;
            const percentage = ((totalScore / (maxPoints || 1)) * 100).toFixed(1);
            const penaltyApplied = scoreCalculation.penaltyApplied;

            // Generate attachments HTML (UNCHANGED)
            const attachmentsHTML = gradeData.attachments?.map((att, index) => {
                if (att.base64Data) {
                    return `<div class="attachment-item"><img src="${att.base64Data}" alt="${att.name}" class="clickable-image" data-index="${index}" style="max-width: 200px; max-height: 200px; object-fit: contain; display: block; margin-bottom: 0.5rem; border: 1px solid #ddd; border-radius: 4px;" /><div style="font-size: 0.875rem; font-weight: 500; word-break: break-word;">${att.name}</div><div style="font-size: 0.75rem; color: #666;">${(att.size / 1024).toFixed(1)} KB</div><div style="font-size: 0.75rem; color: #007bff; margin-top: 4px;">Click to enlarge</div></div>`;
                } else {
                    return `<div class="attachment-item"><div style="width: 40px; height: 40px; background: #f3f4f6; border-radius: 4px; display: flex; align-items: center; justify-content: center; margin: 0 auto 0.5rem auto;">📄</div><div style="font-size: 0.875rem; font-weight: 500; word-break: break-word;">${att.name}</div><div style="font-size: 0.75rem; color: #666;">${(att.size / 1024).toFixed(1)} KB</div></div>`;
                }
            }).join('') || '';

            // Generate video links HTML (UNCHANGED)
            const videoLinksHTML = gradeData.videoLinks?.map(link =>
                `<div class="video-link-item" style="background: #f8f9fa; border: 1px solid #e9ecef; border-radius: 8px; padding: 1rem; margin-bottom: 1rem;"><div style="display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.5rem;"><span style="font-size: 1.25rem;">🎥</span><strong style="color: #495057;">${link.title}</strong></div><a href="${link.url}" target="_blank" rel="noopener noreferrer" style="color: #007bff; text-decoration: none; word-break: break-all;">${link.url}</a></div>`
            ).join('') || '';

            // Generate rubric table HTML (UNCHANGED)
            const rubricTableHTML = sharedRubric ? `
    <div style="margin: 30px 0;">
        <h3>📊 Detailed Rubric Assessment</h3>
        <table style="width: 100%; border-collapse: collapse; margin-top: 15px; font-size: 0.9rem;">
            <thead>
                <tr style="background: #f8f9fa;">
                    <th style="border: 1px solid #ddd; padding: 12px; text-align: left;">Criterion</th>
                    <th style="border: 1px solid #ddd; padding: 12px; text-align: center; width: 120px;">Level Achieved</th>
                    <th style="border: 1px solid #ddd; padding: 12px; text-align: center; width: 80px;">Points</th>
                    <th style="border: 1px solid #ddd; padding: 12px; text-align: left;">Details & Comments</th>
                </tr>
            </thead>
            <tbody>
                ${sharedRubric.criteria.map(criterion => {
                const rubricGrading = gradeData.rubricGrading?.[criterion.id];
                const selectedLevel = rubricGrading?.selectedLevel;
                const level = selectedLevel ? sharedRubric.rubricLevels.find(l => l.level === selectedLevel) : null;
                const levelDef = selectedLevel ? criterion.levels?.[selectedLevel] : null;
                const points = calculateLevelPoints({
                  criterion,
                  levelKey: selectedLevel,
                  rubricLevels: sharedRubric.rubricLevels,
                  customPoints: rubricGrading?.customPoints
                });
                const levelDescription = levelDef?.description || '';
                const additionalComments = rubricGrading?.customComments || '';
                const { background: levelColor, text: levelTextColor } = getLevelBadgeColors(level?.color);

                return `<tr>
                            <td style="border: 1px solid #ddd; padding: 12px; vertical-align: top;">
                                <strong>${criterion.name}</strong><br>
                                <small style="color: #666;">${criterion.description || ''}</small><br>
                                <small style="color: #888;">Max Points: ${criterion.maxPoints}</small>
                            </td>
                            <td style="border: 1px solid #ddd; padding: 8px; text-align: center; vertical-align: top;">
                                <span style="display: inline-block; padding: 4px 8px; border-radius: 4px; font-size: 0.8rem; font-weight: bold; background: ${levelColor}; color: ${levelTextColor}; border: 1px solid ${levelColor};">
                                    ${level?.name || 'Not Assessed'}
                                </span>
                            </td>
                            <td style="border: 1px solid #ddd; padding: 8px; text-align: center; font-weight: bold; font-size: 1.1em; color: #2c3e50;">${points}</td>
                            <td style="border: 1px solid #ddd; padding: 8px; vertical-align: top;">
                                ${levelDescription ? `<div style="margin-bottom: 10px; padding: 8px; background: #f8f9fa; border-left: 4px solid ${levelColor}; border-radius: 4px;"><strong style="color: ${levelColor};">Level Description:</strong><br><span style="font-size: 0.85em; line-height: 1.4;">${levelDescription}</span></div>` : ''}
                                ${additionalComments ? `<div style="margin-top: 8px; padding: 8px; background: #fff3cd; border: 1px solid #ffeaa7; border-radius: 4px;"><strong style="color: #856404;">Additional Comments:</strong><br><span style="font-size: 0.85em; line-height: 1.4; white-space: pre-wrap;">${additionalComments}</span></div>` : ''}
                                ${(!levelDescription && !additionalComments) ? '<em style="color: #999;">No assessment provided</em>' : ''}
                            </td>
                        </tr>`;
            }).join('')}
            </tbody>
        </table>
        <div style="background: #e8f5e8; border: 1px solid #4caf50; border-radius: 8px; padding: 15px; margin-top: 20px;">
            <h4 style="color: #2e7d32; margin-bottom: 10px;">📊 Rubric Score Summary</h4>
            <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 15px;">
                <div><strong>Total Score:</strong> ${Math.round(totalScore * 10) / 10} / ${maxPoints}<br><strong>Percentage:</strong> ${percentage}%</div>
                <div><strong>Grade Status:</strong><span style="color: ${percentage >= sharedRubric.assignmentInfo.passingThreshold ? '#4caf50' : '#f44336'}; font-weight: bold;">${percentage >= sharedRubric.assignmentInfo.passingThreshold ? '✓ PASSING' : '✗ NEEDS IMPROVEMENT'}</span></div>
                ${penaltyApplied ? `<div style="color: #ff9800;"><strong>Late Penalty Applied:</strong><br>Raw Score: ${Math.round(rawScore * 10) / 10}</div>` : ''}
            </div>
        </div>
    </div>` : '';

            // NEW: Generate header with logo using utility
            const reportData = {
                title: `📄 Individual Grade Report: ${student.name}`,
                courseCode: classList.courseMetadata?.courseCode || gradeData.course?.code || '',
                courseName: classList.courseMetadata?.courseName || gradeData.course?.name || '',
                section: classList.courseMetadata?.section || '',
                rubricTitle: sharedRubric?.assignmentInfo?.title || gradeData.assignment?.name || 'Unnamed Rubric'
            };

            const headerHtml = generateReportHeader(reportData, { maxHeight: 60 });

            // Complete HTML document with logo integration
            const htmlContent = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Grade Report - ${student.name}</title>
    ${getReportStyles()}
    <style>
        /* Additional styles specific to individual reports */
        .score-summary { 
            background: #e8f5e8; 
            border: 2px solid #4caf50; 
            border-radius: 8px; 
            padding: 20px; 
            margin: 20px 0; 
            text-align: center; 
        }
        .late-policy-section { 
            margin: 30px 0; 
            background: #fff5f5; 
            border: 1px solid #f87171; 
            border-radius: 8px; 
            padding: 20px; 
        }
        .feedback-section { 
            margin: 20px 0; 
            padding: 15px; 
            background: #f9f9f9; 
            border-radius: 8px; 
        }
        .attachments { 
            margin: 30px 0; 
        }
        .attachment-item { 
            display: inline-block; 
            text-align: center; 
            margin: 1rem; 
            padding: 1rem; 
            background: #fff; 
            border: 1px solid #ddd; 
            border-radius: 8px; 
            box-shadow: 0 2px 4px rgba(0,0,0,.1); 
            max-width: 250px; 
            vertical-align: top; 
        }
        .clickable-image { 
            cursor: pointer; 
            transition: all .3s ease; 
            position: relative; 
        }
        .clickable-image:hover { 
            transform: scale(1.05); 
            box-shadow: 0 4px 8px rgba(0,0,0,.2); 
        }
        .video-links { 
            margin: 30px 0; 
        }
        .video-link-item { 
            margin-bottom: 1rem; 
        }
        .video-link-item a { 
            color: #007bff; 
            text-decoration: none; 
        }
        .video-link-item a:hover { 
            text-decoration: underline; 
        }
        .image-modal { 
            display: none; 
            position: fixed; 
            z-index: 1000; 
            left: 0; 
            top: 0; 
            width: 100%; 
            height: 100%; 
            background-color: rgba(0,0,0,.9); 
            animation: fadeIn .3s ease; 
        }
        .image-modal.show { 
            display: flex; 
            align-items: center; 
            justify-content: center; 
        }
        .modal-content { 
            max-width: 95%; 
            max-height: 95%; 
            object-fit: contain; 
            border-radius: 8px; 
            box-shadow: 0 4px 20px rgba(0,0,0,.5); 
            animation: zoomIn .3s ease; 
        }
        .close-modal { 
            position: absolute; 
            top: 20px; 
            right: 30px; 
            color: #fff; 
            font-size: 40px; 
            font-weight: 700; 
            cursor: pointer; 
            z-index: 1001; 
            background: rgba(0,0,0,.5); 
            border-radius: 50%; 
            width: 50px; 
            height: 50px; 
            display: flex; 
            align-items: center; 
            justify-content: center; 
            line-height: 1; 
        }
        .close-modal:hover { 
            background: rgba(0,0,0,.8); 
        }
        .modal-caption { 
            position: absolute; 
            bottom: 20px; 
            left: 50%; 
            transform: translateX(-50%); 
            color: #fff; 
            background: rgba(0,0,0,.7); 
            padding: 10px 20px; 
            border-radius: 6px; 
            text-align: center; 
            max-width: 80%; 
        }
        @keyframes fadeIn { 
            from { opacity: 0; } 
            to { opacity: 1; } 
        }
        @keyframes zoomIn { 
            from { transform: scale(.5); opacity: 0; } 
            to { transform: scale(1); opacity: 1; } 
        }
        @media print { 
            .attachment-item, .video-link-item { 
                break-inside: avoid; 
            } 
            .image-modal { 
                display: none!important; 
            } 
        }
    </style>
</head>
<body>
    ${headerHtml}

    <div class="score-summary">
        <h2>📊 Final Score</h2>
        <div style="font-size: 2rem; font-weight: 700; color: #2e7d32; margin: 15px 0;">
            ${totalScore.toFixed(1)} / ${maxPoints} (${percentage}%)
        </div>
        <p style="margin: 10px 0; color: #555;">
            ${sharedRubric ? `Rubric: ${sharedRubric.assignmentInfo.title}` : ""}
            ${penaltyApplied ? ` | Late Policy: ${getSafeLatePolicy(gradeData.course?.code).name}` : ""}
        </p>
    </div>

    ${penaltyApplied ? `
        <div class="late-policy-section">
            <h3 style="color: #dc2626;">📅 Late Submission Policy Applied</h3>
            <p><strong>Policy Status:</strong> ${getSafeLatePolicy(gradeData.course?.code).name}</p>
            <p>${getSafeLatePolicy(gradeData.course?.code).description}</p>
            <p><strong>Raw Score:</strong> ${Math.round(rawScore * 10) / 10}/${maxPoints} → <strong>Final Score:</strong> ${Math.round(totalScore * 10) / 10}/${maxPoints}</p>
        </div>
    ` : ""}

    ${rubricTableHTML}

    ${Object.entries(gradeData.feedback || {}).filter(([key, text]) => text).map(([key, text]) => `
        <div class="feedback-section">
            <h3>${key.charAt(0).toUpperCase() + key.slice(1)} Feedback</h3>
            <p>${text.replace(/\n/g, "<br>")}</p>
        </div>
    `).join("")}

    ${attachmentsHTML ? `
        <div class="attachments">
            <h3>📎 File Attachments</h3>
            <div style="display: flex; flex-wrap: wrap; justify-content: flex-start;">
                ${attachmentsHTML}
            </div>
        </div>
    ` : ""}

    ${videoLinksHTML ? `
        <div class="video-links">
            <h3>🎥 Video Review Links</h3>
            ${videoLinksHTML}
        </div>
    ` : ""}

    <div class="footer">
        <div style="margin-top: 40px; text-align: center; font-size: 12px; color: #666;">
            <strong>Individual Grade Report</strong><br>
            Report generated on ${new Date().toLocaleDateString('en-CA')} at ${new Date().toLocaleTimeString('en-CA')}<br>
            Student: ${student.name} | Assignment: ${sharedRubric?.assignmentInfo?.title || 'Unnamed Assignment'}
            ${hasSchoolLogo() ? '<br>Generated with institutional branding' : ''}
        </div>
    </div>

    <div id="imageModal" class="image-modal">
        <span class="close-modal" onclick="closeImageModal()">&times;</span>
        <img class="modal-content" id="modalImage">
        <div class="modal-caption" id="modalCaption"></div>
    </div>

    <script>
        const attachmentData = ${JSON.stringify(gradeData.attachments || [])};
        
        function openImageModal(src, caption) {
            const modal = document.getElementById("imageModal");
            const modalImg = document.getElementById("modalImage");
            const modalCaption = document.getElementById("modalCaption");
            modal.classList.add("show");
            modalImg.src = src;
            modalCaption.textContent = caption;
            document.body.style.overflow = "hidden";
        }
        
        function closeImageModal() {
            document.getElementById("imageModal").classList.remove("show");
            document.body.style.overflow = "auto";
        }
        
        document.addEventListener("DOMContentLoaded", function() {
            document.querySelectorAll(".clickable-image").forEach(img => {
                img.addEventListener("click", function() {
                    const index = parseInt(this.getAttribute("data-index"));
                    const attachment = attachmentData[index];
                    if (attachment && attachment.base64Data) {
                        openImageModal(attachment.base64Data, attachment.name);
                    }
                });
            });
            
            document.getElementById("imageModal").addEventListener("click", function(e) {
                if (e.target === this) {
                    closeImageModal();
                }
            });
            
            document.addEventListener("keydown", function(e) {
                if (e.key === "Escape") {
                    closeImageModal();
                }
            });
        });
    </script>
</body>
</html>`;

            return htmlContent;
        };

        // 7. Generate and export the HTML file
        try {
            const htmlContent = generateStudentReportHTML();
            const htmlBlob = new Blob([htmlContent], { type: 'text/html' });
            const url = URL.createObjectURL(htmlBlob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `grade_report_${student.name.replace(/[^a-zA-Z0-9]/g, '_')}_${sharedRubric.assignmentInfo?.title?.replace(/[^a-zA-Z0-9]/g, '_') || 'assignment'}.html`;
            link.click();
            URL.revokeObjectURL(url);

            console.log(`✅ Individual grade report exported for student: ${student.name}`);
        } catch (error) {
            console.error('Error generating individual grade report:', error);
            alert('Error generating grade report. Please check the console for details.');
        }
    };

    const isExcelFile = (file) => file && /\.xlsx?$/.test(file.name.toLowerCase());

    const processClassListFile = async (file) => {
        if (!file) return;

        if (!isExcelFile(file)) {
            setImportStatus('error');
            setTimeout(() => setImportStatus(''), 3000);
            return;
        }

        setImportStatus('processing');

        try {
            // Use Excel parser instead of manual CSV parsing
            const result = await parseExcelFile(file);

            if (!result.success) {
                throw new Error(result.error || 'Failed to parse Excel file');
            }

            // Validate the parsed student data
            const validation = validateStudentData(result.students);

            if (result.students.length === 0) {
                throw new Error('No valid student records found in Excel file');
            }

            // Create grading progress tracking for each student
            const gradingProgress = result.students.map(() => ({
                status: 'pending',
                lastModified: null,
                gradeType: null
            }));

            // ENHANCED: Create the class list data structure with proper field mapping
            const classListData = {
                students: result.students,
                gradingProgress,
                fileName: file.name,
                fileSize: file.size,
                importTime: new Date().toISOString(),
                courseMetadata: {
                    courseCode: result.courseMetadata?.courseCode || 'IMPORTED',
                    courseName: result.courseMetadata?.courseName || 'Excel Import',
                    section: result.courseMetadata?.section || 'DEFAULT',
                    programType: result.courseMetadata?.programType || 'degree',
                    // ENHANCED: Handle both instructor and professors fields
                    professors: result.courseMetadata?.professors || 'TBD',
                    instructor: result.courseMetadata?.instructor || result.courseMetadata?.professors || 'TBD',
                    term: result.courseMetadata?.term || 'TBD',
                    hours: result.courseMetadata?.hours || '',
                    gradeScale: result.courseMetadata?.gradeScale || '',
                    department: result.courseMetadata?.department || '',
                    campus: result.courseMetadata?.campus || '',
                    totalStudents: result.students.length
                },
                validation: {
                    validationScore: validation.validationScore || 100,
                    issues: validation.issues || []
                }
            };

            setClassList(classListData);
            setImportStatus('success');

            // ENHANCED: Auto-populate course information using corrected field mapping
            if (result.courseMetadata) {
                const courseInfo = {
                    code: result.courseMetadata.courseCode || '',
                    name: result.courseMetadata.courseName || '',
                    instructor: result.courseMetadata.instructor || result.courseMetadata.professors || '',
                    term: result.courseMetadata.term || ''
                };

                console.log('📊 Auto-populating course info from Excel:', courseInfo);
                // Note: updateCourseInfo should be available from useAssessment hook
                if (typeof updateCourseInfo === 'function') {
                    Object.entries(courseInfo).forEach(([field, value]) => {
                        updateCourseInfo(field, value);
                    });
                }
            }

            console.log('✅ Excel import completed successfully:', {
                students: result.students.length,
                courseMetadata: classListData.courseMetadata
            });

            setTimeout(() => setImportStatus(''), 3000);
        } catch (error) {
            console.error('Excel import error:', error);
            setImportStatus('error');
            setTimeout(() => setImportStatus(''), 3000);
        }
    };

    // Reload class list: merge new students from an updated Excel file
    const processReloadClassList = async (file) => {
        if (!file || !classList) return;

        if (!isExcelFile(file)) {
            setReloadResult({ success: false, message: 'Please upload a valid Excel file (.xls or .xlsx).' });
            return;
        }

        setIsReloading(true);

        try {
            const result = await parseExcelFile(file);

            if (!result.success) {
                throw new Error(result.error || 'Failed to parse Excel file');
            }

            if (result.students.length === 0) {
                throw new Error('No valid student records found in the uploaded file.');
            }

            const validation = validateStudentData(result.students);

            // Build a set of existing student IDs for fast lookup
            const existingIds = new Set(classList.students.map(s => s.id));

            // Identify new students (present in uploaded file but not in current roster)
            const newStudents = result.students.filter(s => !existingIds.has(s.id));

            // Identify students who were removed (present in current roster but not in uploaded file)
            const uploadedIds = new Set(result.students.map(s => s.id));
            const removedStudents = classList.students.filter(s => !uploadedIds.has(s.id));

            // Identify students whose info has been updated (corrections)
            const updatedStudents = [];
            result.students.forEach(newStudent => {
                if (!existingIds.has(newStudent.id)) return;
                const existing = classList.students.find(s => s.id === newStudent.id);
                if (!existing) return;
                const changed = ['name', 'email', 'program', 'level', 'campus', 'status', 'component']
                    .some(field => newStudent[field] && newStudent[field] !== existing[field] && newStudent[field].trim() !== '');
                if (changed) {
                    updatedStudents.push({ before: existing, after: newStudent });
                }
            });

            if (newStudents.length === 0 && updatedStudents.length === 0 && removedStudents.length === 0) {
                setReloadResult({
                    success: true,
                    message: 'No changes detected. The uploaded roster matches the current class list.',
                    added: 0,
                    updated: 0,
                    removed: 0,
                    removedNames: [],
                    total: classList.students.length,
                });
                setIsReloading(false);
                return;
            }

            // Merge: append new students and their grading progress entries
            const mergedStudents = [...classList.students];
            const mergedProgress = [...classList.gradingProgress];

            // Add new students
            newStudents.forEach(student => {
                mergedStudents.push(student);
                mergedProgress.push({
                    status: 'pending',
                    lastModified: null,
                    gradeType: null
                });
            });

            // Apply corrections to existing students
            updatedStudents.forEach(({ after }) => {
                const idx = mergedStudents.findIndex(s => s.id === after.id);
                if (idx >= 0) {
                    // Update fields but preserve existing student's id
                    mergedStudents[idx] = {
                        ...mergedStudents[idx],
                        name: after.name || mergedStudents[idx].name,
                        email: after.email || mergedStudents[idx].email,
                        program: after.program || mergedStudents[idx].program,
                        level: after.level || mergedStudents[idx].level,
                        campus: after.campus || mergedStudents[idx].campus,
                        status: after.status || mergedStudents[idx].status,
                        component: after.component || mergedStudents[idx].component,
                    };
                }
            });

            // Update class list preserving all existing data
            setClassList(prev => ({
                ...prev,
                students: mergedStudents,
                gradingProgress: mergedProgress,
                courseMetadata: {
                    ...prev.courseMetadata,
                    totalStudents: mergedStudents.length,
                },
                reloadHistory: [
                    ...(prev.reloadHistory || []),
                    {
                        fileName: file.name,
                        reloadTime: new Date().toISOString(),
                        added: newStudents.length,
                        updated: updatedStudents.length,
                    }
                ],
            }));

            // Sync new and updated students into the GradeBook if it's already loaded
            if (gradeBook && (newStudents.length > 0 || updatedStudents.length > 0)) {
                setGradeBook(prev => {
                    if (!prev) return prev;

                    const existingGbIds = new Set(prev.students.map(s => s.id));
                    const projectIds = (prev.projects || []).map(p => p.id);

                    // Build new students for the gradeBook
                    const gbNewStudents = newStudents
                        .filter(s => !existingGbIds.has(s.id))
                        .map(s => ({
                            id: s.id,
                            name: s.name,
                            email: s.email || '',
                            studentNumber: s.studentNumber || '',
                            program: s.program || '',
                        }));

                    // Initialize empty grade entries for new students (null per project)
                    const newGrades = {};
                    gbNewStudents.forEach(s => {
                        newGrades[s.id] = projectIds.reduce((acc, pid) => {
                            acc[pid] = null;
                            return acc;
                        }, {});
                    });

                    // Apply corrections to existing gradeBook students
                    const updatedGbStudents = prev.students.map(s => {
                        const correction = updatedStudents.find(u => u.after.id === s.id);
                        if (!correction) return s;
                        return {
                            ...s,
                            name: correction.after.name || s.name,
                            email: correction.after.email || s.email,
                            program: correction.after.program || s.program,
                        };
                    });

                    return {
                        ...prev,
                        students: [...updatedGbStudents, ...gbNewStudents],
                        grades: { ...prev.grades, ...newGrades },
                        metadata: {
                            ...prev.metadata,
                            lastModified: new Date().toISOString(),
                        },
                    };
                });

                console.log('✅ GradeBook synced with reload:', {
                    newStudentsAdded: newStudents.length,
                    studentsUpdated: updatedStudents.length,
                });
            }

            // Sync the merged roster into the active grading session and grade book
            // so that the Grading Tool counts and Grade Book rows stay in sync.
            syncRosterAcrossSessionAndGradebook({
                mergedStudents,
                mergedProgress,
                courseMetadata: {
                    ...classList.courseMetadata,
                    totalStudents: mergedStudents.length,
                },
                gradingSession,
                setGradingSession,
                gradeBook,
                setGradeBook,
            });

            setReloadResult({
                success: true,
                message: `Roster updated successfully.`,
                added: newStudents.length,
                updated: updatedStudents.length,
                removed: removedStudents.length,
                removedNames: removedStudents.map(s => s.name),
                newNames: newStudents.map(s => s.name),
                updatedNames: updatedStudents.map(u => u.after.name),
                total: mergedStudents.length,
                fileName: file.name,
            });

            console.log('✅ Class list reload completed:', {
                added: newStudents.length,
                updated: updatedStudents.length,
                totalNow: mergedStudents.length,
            });
        } catch (error) {
            console.error('Reload class list error:', error);
            setReloadResult({
                success: false,
                message: error.message || 'Failed to reload class list.',
            });
        } finally {
            setIsReloading(false);
        }
    };

    const handleReloadUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;
        await processReloadClassList(file);
        // Reset the input so the same file can be re-selected
        if (reloadFileInputRef.current) {
            reloadFileInputRef.current.value = '';
        }
    };

    // File upload handling (preserved exactly from original)
    // COMPLETE ENHANCED handleFileUpload function - replaces lines ~600-650 in ClassListManager.js
    const handleFileUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        await processClassListFile(file);
    };

    const handleDrop = async (event) => {
        event.preventDefault();
        setIsDragActive(false);

        const { dataTransfer } = event;
        let file = null;

        if (dataTransfer.items && dataTransfer.items.length > 0) {
            for (let i = 0; i < dataTransfer.items.length; i++) {
                const item = dataTransfer.items[i];
                if (item.kind === 'file') {
                    const droppedFile = item.getAsFile();
                    if (isExcelFile(droppedFile)) {
                        file = droppedFile;
                        break;
                    }
                }
            }
        }

        if (!file && dataTransfer.files && dataTransfer.files.length > 0) {
            file = Array.from(dataTransfer.files).find(isExcelFile) || dataTransfer.files[0];
        }

        await processClassListFile(file);
    };

    const handleDragOver = (event) => {
        event.preventDefault();
        if (event.dataTransfer) {
            event.dataTransfer.dropEffect = 'copy';
        }
        setIsDragActive(true);
    };

    const handleDragLeave = () => {
        setIsDragActive(false);
    };

    // ENHANCED startGradingSession function - replaces lines ~670-690 in ClassListManager.js
    const startGradingSession = () => {
        if (!classList || classList.students.length === 0) {
            alert('No students available for grading.');
            return;
        }

        if (!sharedRubric) {
            alert('Please load a rubric before starting batch grading.');
            return;
        }

        console.log('🚀 Starting grading session with class data:', {
            studentCount: classList.students.length,
            courseMetadata: classList.courseMetadata,
            rubricTitle: sharedRubric.assignmentInfo?.title
        });

        // Initialize the grading session with enhanced data
        const success = initializeGradingSession(classList);

        if (success) {
            setActiveTab('grading-tool');

            // Show enhanced success message with course info confirmation
            const courseInfo = classList.courseMetadata;
            alert(
                `🎯 Batch Grading Started!\n\n` +
                `Rubric: ${sharedRubric.assignmentInfo?.title || 'Untitled'}\n` +
                `Students: ${classList.students.length}\n` +
                `Course: ${courseInfo?.courseCode || 'N/A'} - ${courseInfo?.courseName || 'N/A'}\n` +
                `Course Professor: ${courseInfo?.instructor || courseInfo?.professors || 'N/A'}\n` +
                `${gradingData?.course?.evaluator ? `Course Evaluator: ${gradingData.course.evaluator}\n` : ''}` +
                `Term: ${courseInfo?.term || 'N/A'}\n\n` +
                `Starting with: ${classList.students[0]?.name || 'First Student'}`
            );
        } else {
            alert('Failed to initialize grading session. Please check the console for details.');
        }
    };

    // Pause the grading session
    const pauseGradingSession = () => {
        setGradingSession(prev => ({
            ...prev,
            active: false
        }));
    };

    // Resume the grading session
    const resumeGradingSession = () => {
        setGradingSession(prev => ({
            ...prev,
            active: true
        }));
        setActiveTab('grading-tool');
    };

    // NEW: Policy Preview Component
    const PolicyPreview = ({ policy }) => {
        if (!policy) return null;

        const gradeEntries = (policy.gradeScale || []).map(grade => ({
            label: grade.letter || grade.alphaGrade || grade.label,
            min: grade.minPercentage ?? grade.lowPercent ?? 0,
            max: grade.maxPercentage ?? grade.highPercent ?? null,
            gpa: grade.gradePoint ?? grade.gpa
        }));

        const visibleGrades = gradeEntries.slice(0, 4);
        const remainingCount = Math.max(gradeEntries.length - visibleGrades.length, 0);

        const associatedPrograms = (policy.associatedProgramTypes || []).join(', ');

        return (
            <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <h4 className="font-semibold text-blue-800 mb-2 text-sm">
                    📋 Active Grading Policy: {policy.name}
                </h4>
                <p className="text-xs text-blue-700 mb-2">{policy.description}</p>

                {associatedPrograms && (
                    <p className="text-[11px] text-blue-700 mb-2">Programs: {associatedPrograms}</p>
                )}

                <div className="grid grid-cols-2 md:grid-cols-4 gap-1 text-xs">
                    {visibleGrades.map((grade, index) => (
                        <div key={index} className="bg-white p-1 rounded border text-center">
                            <div className="font-bold text-blue-800">{grade.label}</div>
                            <div className="text-blue-600">
                                {grade.max ? `${grade.min}% - ${grade.max}%` : `${grade.min}%+`}
                            </div>
                            {grade.gpa !== undefined && (
                                <div className="text-[10px] text-blue-500">GPA {grade.gpa}</div>
                            )}
                        </div>
                    ))}
                </div>

                {remainingCount > 0 && (
                    <div className="text-xs text-blue-600 mt-1">
                        ...and {remainingCount} more grades
                    </div>
                )}

                <div className="mt-2 text-[11px] text-blue-700">Class List Manager will apply this policy for the selected program type.</div>

                <button
                    onClick={() => {
                        gradingPolicyService.setLastViewedPolicyId(policy.id);
                        setActiveTab('policy-manager');
                    }}
                    className="mt-2 inline-flex items-center gap-2 text-xs text-blue-800 underline"
                >
                    View full policy in Policy Manager
                </button>
            </div>
        );
    };

    // Enhanced program type dropdown with policy support
    const renderEnhancedProgramTypeDropdown = () => {
        const supported = gradingPolicyService.getSupportedProgramTypes();
        const activeProgramTypes = supported.filter(type =>
            availablePolicies.some(policy =>
                (policy.associatedProgramTypes || []).includes(type.value)
            )
        );

        return (
            <div>
                <label htmlFor="programType" className="block text-sm font-bold text-gray-700">Program Type:</label>
                <select
                    id="programType"
                    name="programType"
                    value={normalizeProgramTypeValue(classList.courseMetadata?.programType || currentProgramType)}
                    onChange={handleProgramTypeChange}
                    className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                >
                    {activeProgramTypes.map(type => (
                        <option key={type.value} value={type.value}>
                            {type.label}
                        </option>
                    ))}
                </select>

                {/* Policy Preview */}
                {selectedPolicy && <PolicyPreview policy={selectedPolicy} />}

                {/* Policy Loading Indicator */}
                {(policiesLoading || gradeCalculation.isPending) && (
                    <div className="flex items-center gap-2 text-blue-600 mt-2">
                        <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                        <span className="text-xs">
                            {policiesLoading ? 'Loading grading policies...' : 'Calculating grade...'}
                        </span>
                    </div>
                )}
            </div>
        );
    };

    return (
        <>
            {showResetConfirm && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6 space-y-4">
                        <div className="flex items-center gap-2 text-orange-700">
                            <RefreshCcw size={20} />
                            <h2 className="text-xl font-semibold">Reset Grading Status</h2>
                        </div>
                        <div className="text-sm text-gray-700 space-y-2">
                            <p>This will reset the grading status for all students in this class to Not Started.</p>
                            <p>Existing grades will not be deleted, but grading progress indicators will be reset.</p>
                        </div>
                        <div className="flex justify-end gap-2">
                            <button
                                onClick={() => setShowResetConfirm(false)}
                                className="px-4 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-100 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => {
                                    resetGradingStatuses();
                                    setShowResetConfirm(false);
                                }}
                                className="px-4 py-2 rounded bg-orange-600 text-white hover:bg-orange-700 transition-colors"
                            >
                                Confirm Reset
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {reloadResult && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6 space-y-4 max-h-[80vh] overflow-y-auto">
                        <div className="flex items-center gap-2">
                            {reloadResult.success ? (
                                <CheckCircle2 size={20} className="text-green-600" />
                            ) : (
                                <AlertCircle size={20} className="text-red-600" />
                            )}
                            <h2 className="text-xl font-semibold text-gray-900">
                                {reloadResult.success ? 'Roster Reload Complete' : 'Reload Failed'}
                            </h2>
                        </div>

                        <p className="text-sm text-gray-700">{reloadResult.message}</p>

                        {reloadResult.success && (reloadResult.added > 0 || reloadResult.updated > 0 || reloadResult.removed > 0) && (
                            <div className="space-y-3">
                                {/* Summary stats */}
                                <div className="grid grid-cols-3 gap-3">
                                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                                        <div className="text-2xl font-bold text-green-700">{reloadResult.added}</div>
                                        <div className="text-xs text-green-600 font-medium">Added</div>
                                    </div>
                                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
                                        <div className="text-2xl font-bold text-blue-700">{reloadResult.updated}</div>
                                        <div className="text-xs text-blue-600 font-medium">Updated</div>
                                    </div>
                                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
                                        <div className="text-2xl font-bold text-gray-700">{reloadResult.total}</div>
                                        <div className="text-xs text-gray-600 font-medium">Total Now</div>
                                    </div>
                                </div>

                                {/* New students list */}
                                {reloadResult.newNames && reloadResult.newNames.length > 0 && (
                                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                                        <h4 className="text-sm font-semibold text-green-800 mb-1">New Students Added:</h4>
                                        <ul className="text-sm text-green-700 space-y-0.5">
                                            {reloadResult.newNames.map((name, i) => (
                                                <li key={i} className="flex items-center gap-1.5">
                                                    <span className="w-1.5 h-1.5 bg-green-500 rounded-full flex-shrink-0"></span>
                                                    {name}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {/* Updated students list */}
                                {reloadResult.updatedNames && reloadResult.updatedNames.length > 0 && (
                                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                                        <h4 className="text-sm font-semibold text-blue-800 mb-1">Students Updated (info corrections):</h4>
                                        <ul className="text-sm text-blue-700 space-y-0.5">
                                            {reloadResult.updatedNames.map((name, i) => (
                                                <li key={i} className="flex items-center gap-1.5">
                                                    <span className="w-1.5 h-1.5 bg-blue-500 rounded-full flex-shrink-0"></span>
                                                    {name}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {/* Removed students notice */}
                                {reloadResult.removed > 0 && (
                                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                                        <h4 className="text-sm font-semibold text-yellow-800 mb-1">
                                            Students not in updated file ({reloadResult.removed}):
                                        </h4>
                                        <p className="text-xs text-yellow-700 mb-1">
                                            These students were kept in the roster to preserve their existing grades.
                                        </p>
                                        <ul className="text-sm text-yellow-700 space-y-0.5">
                                            {reloadResult.removedNames.map((name, i) => (
                                                <li key={i} className="flex items-center gap-1.5">
                                                    <span className="w-1.5 h-1.5 bg-yellow-500 rounded-full flex-shrink-0"></span>
                                                    {name}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="flex justify-end pt-2">
                            <button
                                onClick={() => setReloadResult(null)}
                                className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 transition-colors font-medium"
                            >
                                OK
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
                <div className="max-w-7xl mx-auto px-4 py-6">
                {/* Page Header - Improved visual hierarchy */}
                <div className="bg-white border-b-4 border-blue-600 rounded-t-xl shadow-md mb-6">
                    <div className="px-6 py-5">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                            <div className="flex items-center gap-4">
                                <div className="p-3 bg-blue-100 rounded-xl">
                                    <Users size={32} className="text-blue-700" />
                                </div>
                                <div>
                                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1">
                                        Class List Manager
                                    </h1>
                                    <p className="text-sm text-gray-600">
                                        Import, manage, and grade entire classes efficiently
                                    </p>
                                </div>
                            </div>
                            {classList && (
                                <div className="flex items-center gap-3 px-4 py-3 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                                    <div className="text-center">
                                        <div className="text-3xl font-bold text-blue-900">
                                            {classList.students.length}
                                        </div>
                                        <div className="text-xs text-blue-700 font-medium uppercase tracking-wide">
                                            Student{classList.students.length !== 1 ? 's' : ''}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-b-xl shadow-lg overflow-hidden">
                    {/* Import Section (preserved exactly from original) */}
                    {!classList && (
                        <div className="p-6 sm:p-8">
                            <div className="grid lg:grid-cols-3 gap-8">

                                {/* Left Column - Upload Area */}
                                <div className="lg:col-span-2">
                                    <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
                                        {/* Upload Header */}
                                        <div className="bg-gradient-to-r from-blue-700 to-indigo-700 px-8 py-6 relative">
                                            {/* Semi-transparent overlay for better text contrast */}
                                            <div className="absolute inset-0 bg-black/20"></div>

                                            <div className="flex items-center space-x-3 relative z-10">
                                                <div className="bg-white/30 rounded-lg p-3 backdrop-blur-sm">
                                                    <FileSpreadsheet size={32} className="text-white" />
                                                </div>
                                                <div>
                                                    <h2 className="text-3xl font-bold text-white drop-shadow-lg">Import Student List</h2>
                                                    <p className="text-gray-100 font-semibold text-lg drop-shadow-md">Upload your Excel file to get started</p>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Drag and Drop Area */}
                                        <div className="p-8">
                                            <div
                                                className={`relative border-2 border-dashed border-gray-300 hover:border-blue-400 hover:bg-gray-50 rounded-xl p-12 text-center transition-all duration-300 ${isDragActive ? 'border-blue-500 bg-blue-50' : ''}`}
                                                onDragOver={handleDragOver}
                                                onDrop={handleDrop}
                                                onDragEnter={() => setIsDragActive(true)}
                                                onDragLeave={handleDragLeave}
                                                onClick={() => fileInputRef.current?.click()}
                                            >
                                                <input
                                                    type="file"
                                                    accept=".xls,.xlsx"
                                                    onChange={handleFileUpload}
                                                    className="hidden"
                                                    id="excel-upload"
                                                    ref={fileInputRef}
                                                />

                                                <div className="space-y-4">
                                                    <div className="mx-auto w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center">
                                                        <Upload size={32} className="text-gray-400" />
                                                    </div>

                                                    <div>
                                                        <h3 className="text-xl font-semibold text-gray-900 mb-2">
                                                            Drop your Excel file here
                                                        </h3>
                                                        <p className="text-gray-600 mb-6">
                                                            or click to browse and select your student roster
                                                        </p>
                                                    </div>

                                                    <button
                                                        onClick={() => fileInputRef.current?.click()}
                                                        className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-8 rounded-lg transition-colors duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                                                    >
                                                        Choose Excel File
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Status Messages */}
                                            {importStatus === 'loading' && (
                                                <div className="mt-6 flex items-center space-x-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                                                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                                                    <span className="text-blue-800 font-medium">
                                                        📊 Processing Excel file...
                                                    </span>
                                                </div>
                                            )}

                                            {importStatus === 'success' && (
                                                <div className="mt-6 flex items-center space-x-3 p-4 bg-green-50 border border-green-200 rounded-lg">
                                                    <CheckCircle2 size={20} className="text-green-600" />
                                                    <span className="text-green-800 font-medium">
                                                        ✅ Excel file imported successfully!
                                                    </span>
                                                </div>
                                            )}

                                            {importStatus === 'error' && (
                                                <div className="mt-6 flex items-center space-x-3 p-4 bg-red-50 border border-red-200 rounded-lg">
                                                    <AlertCircle size={20} className="text-red-600" />
                                                    <span className="text-red-800 font-medium">
                                                        ✗ Error importing Excel file. Please check format and try again.
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Right Column - Format Requirements */}
                                <div className="space-y-6">

                                    {/* Format Requirements Card */}
                                    <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
                                        <div className="bg-gray-50 px-6 py-4 border-b border-gray-100">
                                            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                                                <FileText size={20} className="mr-2 text-blue-600" />
                                                Excel Format Requirements
                                            </h3>
                                        </div>

                                        <div className="p-6 space-y-4">
                                            <div>
                                                <h4 className="font-medium text-gray-900 mb-2">Required Columns</h4>
                                                <div className="space-y-1">
                                                    {['ID', 'Name', 'Email'].map((col) => (
                                                        <div key={col} className="flex items-center space-x-2">
                                                            <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                                                            <span className="text-sm text-gray-700">{col}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>

                                            <div>
                                                <h4 className="font-medium text-gray-900 mb-2">Optional Columns</h4>
                                                <div className="space-y-1">
                                                    {['Program', 'Campus', 'Level', 'Status'].map((col) => (
                                                        <div key={col} className="flex items-center space-x-2">
                                                            <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                                                            <span className="text-sm text-gray-700">{col}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>

                                            <div className="pt-4 border-t border-gray-100">
                                                <div className="space-y-2 text-sm text-gray-600">
                                                    <div className="flex items-start space-x-2">
                                                        <span className="font-medium">File types:</span>
                                                        <span>.xls or .xlsx</span>
                                                    </div>
                                                    <div className="flex items-start space-x-2">
                                                        <span className="font-medium">Headers:</span>
                                                        <span>First row should contain column names</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Tips Card */}
                                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
                                        <h3 className="text-lg font-semibold text-blue-900 mb-3 flex items-center">
                                            <BookOpen size={20} className="mr-2" />
                                            Pro Tips
                                        </h3>
                                        <ul className="space-y-2 text-sm text-blue-800">
                                            <li className="flex items-start space-x-2">
                                                <span className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
                                                <span>Include all required columns for best results</span>
                                            </li>
                                            <li className="flex items-start space-x-2">
                                                <span className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
                                                <span>Ensure email addresses are properly formatted</span>
                                            </li>
                                            <li className="flex items-start space-x-2">
                                                <span className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
                                                <span>Remove any empty rows or columns</span>
                                            </li>
                                        </ul>
                                    </div>

                                    {/* Sample Template Download */}
                                    <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
                                        <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                                            <Download size={20} className="mr-2 text-green-600" />
                                            Need Help?
                                        </h3>
                                        <p className="text-sm text-gray-600 mb-4">
                                            Download our sample template to ensure your Excel file is formatted correctly.
                                        </p>
                                        <button
                                            onClick={() => {
                                                const link = document.createElement('a');
                                                link.href = '/Example-Class-List.xlsx';
                                                link.download = 'Example-Class-List.xlsx';
                                                link.click();
                                            }}
                                            className="w-full bg-green-100 hover:bg-green-200 text-green-800 font-medium py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
                                        >
                                            <Download size={16} />
                                            Download Sample Template
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Management Dashboard - REDESIGNED for better hierarchy and workflow */}
                    {classList && (
                        <div className="p-6 space-y-6">
                            {/* Top Summary Grid - Responsive 3-card layout */}
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
                                {/* Course Info Card with Enhanced Program Type Dropdown */}
                                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow">
                                    <div className="flex items-center gap-2 mb-3">
                                        <div className="p-2 bg-blue-100 rounded-lg">
                                            <BookOpen className="text-blue-600" size={20} />
                                        </div>
                                        <h3 className="text-base font-semibold text-blue-900">
                                            Course / Policy
                                        </h3>
                                    </div>
                                    <div className="space-y-1.5 text-sm">
                                        <div className="flex justify-between">
                                            <span className="text-blue-700 font-medium">Code:</span>
                                            <span className="text-blue-900 font-semibold">{classList.courseMetadata?.courseCode || 'N/A'}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-blue-700 font-medium">Section:</span>
                                            <span className="text-blue-900">{classList.courseMetadata?.section || 'N/A'}</span>
                                        </div>
                                        <div className="border-t border-blue-200 pt-2 mt-2 space-y-2">
                                            <div>
                                                <label className="block text-xs text-blue-700 font-medium mb-1">Course Professor</label>
                                                <input
                                                    type="text"
                                                    value={gradingData?.course?.instructor || classList.courseMetadata?.instructor || classList.courseMetadata?.professors || ''}
                                                    onChange={(e) => updateCourseInfo('instructor', e.target.value)}
                                                    placeholder="Course Professor"
                                                    className="w-full px-2 py-1.5 text-sm border border-blue-200 rounded-lg bg-white focus:ring-1 focus:ring-blue-400 focus:border-blue-400"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs text-blue-700 font-medium mb-1">Course Evaluator</label>
                                                <input
                                                    type="text"
                                                    value={gradingData?.course?.evaluator || ''}
                                                    onChange={(e) => updateCourseInfo('evaluator', e.target.value)}
                                                    placeholder="Course Evaluator"
                                                    className="w-full px-2 py-1.5 text-sm border border-blue-200 rounded-lg bg-white focus:ring-1 focus:ring-blue-400 focus:border-blue-400"
                                                />
                                            </div>
                                        </div>
                                        <div className="border-t border-blue-200 pt-2 mt-2">
                                            {renderEnhancedProgramTypeDropdown()}
                                        </div>
                                    </div>
                                </div>

                                {/* Grading Progress Card */}
                                <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow">
                                    <div className="flex items-center gap-2 mb-3">
                                        <div className="p-2 bg-green-100 rounded-lg">
                                            <GraduationCap className="text-green-600" size={20} />
                                        </div>
                                        <h3 className="text-base font-semibold text-green-900">
                                            Grading Progress
                                        </h3>
                                    </div>
                                    <div className="space-y-2">
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm text-green-700 font-medium">Total Students</span>
                                            <span className="text-2xl font-bold text-green-900">{classList.students.length}</span>
                                        </div>
                                        <div className="grid grid-cols-2 gap-3 pt-2 border-t border-green-200">
                                            <div className="text-center">
                                                <div className="text-lg font-bold text-green-900">{getGradingProgress().final}</div>
                                                <div className="text-xs text-green-700">Final</div>
                                            </div>
                                            <div className="text-center">
                                                <div className="text-lg font-bold text-yellow-700">{getGradingProgress().draft}</div>
                                                <div className="text-xs text-green-700">Draft</div>
                                            </div>
                                        </div>
                                        <div className="pt-2">
                                            <div className="flex justify-between items-center mb-1">
                                                <span className="text-xs text-green-700 font-medium">Completion</span>
                                                <span className="text-sm font-bold text-green-900">{getGradingProgress().percentage}%</span>
                                            </div>
                                            <div className="w-full bg-green-200 rounded-full h-2">
                                                <div
                                                    className="bg-green-600 h-2 rounded-full transition-all duration-300"
                                                    style={{ width: `${getGradingProgress().percentage}%` }}
                                                ></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Batch Grading Session Card */}
                                <div className="bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow md:col-span-2 lg:col-span-1">
                                    <div className="flex items-center gap-2 mb-3">
                                        <div className="p-2 bg-purple-100 rounded-lg">
                                            <Play className="text-purple-600" size={20} />
                                        </div>
                                        <h3 className="text-base font-semibold text-purple-900">
                                            Batch Grading Session
                                        </h3>
                                    </div>
                                    <div className="space-y-3">
                                        {!gradingSession?.active ? (
                                            <div>
                                                <div className="text-sm text-purple-700 mb-3 min-h-[40px] flex items-center">
                                                    {gradingSession?.currentStudentIndex > 0
                                                        ? `Paused at student ${gradingSession.currentStudentIndex + 1} of ${classList.students.length}`
                                                        : sharedRubric
                                                            ? 'Ready to start batch grading workflow'
                                                            : 'Load a rubric to begin'
                                                    }
                                                </div>
                                                <div className="flex gap-2">
                                                    {gradingSession?.currentStudentIndex > 0 ? (
                                                        <button
                                                            onClick={resumeGradingSession}
                                                            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold transition-colors shadow-sm hover:shadow"
                                                        >
                                                            <Play size={18} />
                                                            Resume Session
                                                        </button>
                                                    ) : (
                                                        <button
                                                            onClick={startGradingSession}
                                                            disabled={!sharedRubric}
                                                            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow"
                                                        >
                                                            <Play size={18} />
                                                            Start Batch Grading
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        ) : (
                                            <div>
                                                <div className="text-sm text-green-700 font-medium mb-3 bg-green-100 rounded-lg p-2 text-center">
                                                    ✓ Active: Student {gradingSession.currentStudentIndex + 1} of {classList.students.length}
                                                </div>
                                                <button
                                                    onClick={pauseGradingSession}
                                                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-semibold transition-colors shadow-sm hover:shadow"
                                                >
                                                    <Clock size={18} />
                                                    Pause Session
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Roster Toolbar - Clean command row for the main work area */}
                            <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
                                <div className="px-5 py-4 bg-gradient-to-r from-gray-50 to-slate-50 border-b border-gray-200">
                                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                        <div className="flex items-center gap-2">
                                            <Users size={20} className="text-gray-700" />
                                            <h3 className="text-lg font-bold text-gray-900">
                                                Student Roster
                                            </h3>
                                            <span className="text-sm text-gray-500">
                                                ({classList.students.length} student{classList.students.length !== 1 ? 's' : ''})
                                            </span>
                                        </div>
                                        <div className="flex flex-wrap items-center gap-2">
                                            {/* Export Actions Group */}
                                            <div className="flex items-center gap-1.5 bg-white rounded-lg border border-gray-200 p-1">
                                                <span className="text-xs font-medium text-gray-600 px-2">Export:</span>
                                                <button
                                                    onClick={async () => {
                                                        console.log('🔄 Starting CSV export...');
                                                        await exportClassGradesCSV();
                                                    }}
                                                    className="flex items-center gap-1 px-2.5 py-1.5 bg-green-50 hover:bg-green-100 text-green-700 rounded text-xs font-medium transition-colors focus:ring-2 focus:ring-green-500"
                                                    title="Export as CSV"
                                                >
                                                    <Download size={14} />
                                                    CSV
                                                </button>
                                                <button
                                                    onClick={async () => {
                                                        console.log('🔄 Starting HTML export...');
                                                        await exportClassGradesHTML();
                                                    }}
                                                    className="flex items-center gap-1 px-2.5 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded text-xs font-medium transition-colors focus:ring-2 focus:ring-blue-500"
                                                    title="Export as HTML"
                                                >
                                                    <FileText size={14} />
                                                    HTML
                                                </button>
                                                <button
                                                    onClick={async () => {
                                                        console.log('🔄 Starting PDF export...');
                                                        await exportClassGradesPortraitPDF();
                                                    }}
                                                    className="flex items-center gap-1 px-2.5 py-1.5 bg-red-50 hover:bg-red-100 text-red-700 rounded text-xs font-medium transition-colors focus:ring-2 focus:ring-red-500"
                                                    title="Export as PDF"
                                                >
                                                    <FileText size={14} />
                                                    PDF
                                                </button>
                                            </div>
                                            <button
                                                onClick={() => {
                                                    sessionStorage.setItem('policyManagerActiveMainTab', 'calculators');
                                                    sessionStorage.setItem('policyManagerCalculatorTab', 'what-if');
                                                    setActiveTab('policy-manager');
                                                }}
                                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100 hover:border-blue-300"
                                                title="Open What-If Calculator"
                                            >
                                                <Calculator size={14} />
                                                what if calculator
                                            </button>
                                            {/* Reload Class List */}
                                            <button
                                                onClick={() => reloadFileInputRef.current?.click()}
                                                disabled={isReloading}
                                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border ${
                                                    isReloading
                                                        ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                                                        : 'bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100 hover:border-indigo-300'
                                                }`}
                                                title="Upload an updated Excel roster to add new students or apply corrections"
                                            >
                                                {isReloading ? (
                                                    <div className="w-3.5 h-3.5 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin"></div>
                                                ) : (
                                                    <Upload size={14} />
                                                )}
                                                {isReloading ? 'Reloading...' : 'Reload Class List'}
                                            </button>
                                            <input
                                                type="file"
                                                accept=".xls,.xlsx"
                                                onChange={handleReloadUpload}
                                                className="hidden"
                                                ref={reloadFileInputRef}
                                            />
                                            {/* Reset Grading Status */}
                                            <button
                                                onClick={() => setShowResetConfirm(true)}
                                                disabled={!hasStudents}
                                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border ${
                                                    hasStudents
                                                        ? 'bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100 hover:border-orange-300'
                                                        : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                                                }`}
                                                title="Reset all grading statuses"
                                            >
                                                <RefreshCcw size={14} />
                                                Reset Status
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Student Roster Table */}
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead className="bg-gray-100 border-y border-gray-200">
                                            <tr>
                                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                                                    #
                                                </th>
                                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                                                    Status
                                                </th>
                                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                                                    Student ID
                                                </th>
                                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                                                    Name
                                                </th>
                                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                                                    Email
                                                </th>
                                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                                                    Program
                                                </th>
                                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide min-w-[240px]">
                                                    Actions
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-100">
                                            {classList.students.map((student, index) => {
                                                const progress = classList.gradingProgress[index];
                                                const isCurrentStudent = gradingSession.active && index === gradingSession.currentStudentIndex;
                                                const gradeStatus = progress?.status === 'not_started' ? 'none' : getGradeStatus(student.id);

                                                return (
                                                    <tr
                                                        key={student.id}
                                                        className={`${isCurrentStudent
                                                            ? 'bg-blue-50 border-l-4 border-blue-500'
                                                            : 'hover:bg-gray-50'
                                                            } transition-colors`}
                                                    >
                                                        <td className="px-4 py-3 text-sm text-gray-700 font-medium">
                                                            {index + 1}
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            {getStatusDisplay(progress, student.id)}
                                                        </td>
                                                        <td className="px-4 py-3 text-sm font-semibold text-gray-900">
                                                            {student.id}
                                                        </td>
                                                        <td className="px-4 py-3 text-sm text-gray-900">
                                                            {student.name}
                                                        </td>
                                                        <td className="px-4 py-3 text-sm text-gray-600">
                                                            {student.email}
                                                        </td>
                                                        <td className="px-4 py-3 text-sm text-gray-600">
                                                            {student.program}
                                                        </td>

                                                        <td className="px-4 py-3">
                                                            <div className="flex flex-wrap items-center gap-1.5">
                                                                {/* Primary Actions Group */}
                                                                <div className="flex items-center gap-1.5">
                                                                    {/* View or Edit - Primary action */}
                                                                    <button
                                                                        onClick={() => loadStudentForGrading(student)}
                                                                        className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-xs font-semibold transition-colors shadow-sm hover:shadow focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                                                                        title={gradeStatus === 'final' ? 'View grade details' : 'Edit grade'}
                                                                    >
                                                                        {gradeStatus === 'final' ? 'View' : 'Edit'}
                                                                    </button>

                                                                    {/* Finalize draft - Primary action when draft exists */}
                                                                    {gradeStatus === 'draft' && (
                                                                        <button
                                                                            onClick={() => finalizeGrade(student.id)}
                                                                            className="flex items-center gap-1 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-md text-xs font-semibold transition-colors shadow-sm hover:shadow focus:ring-2 focus:ring-green-500 focus:ring-offset-1"
                                                                            title="Finalize draft grade"
                                                                        >
                                                                            <CheckCircle size={13} />
                                                                            Finalize
                                                                        </button>
                                                                    )}
                                                                </div>

                                                                {/* Secondary Actions Group */}
                                                                <div className="flex items-center gap-1.5 pl-1 border-l border-gray-200">
                                                                    {/* Export Grade - Secondary action */}
                                                                    <button
                                                                        onClick={() => exportStudentGrade(student.id)}
                                                                        className="flex items-center gap-1 px-2.5 py-1.5 bg-purple-50 hover:bg-purple-100 text-purple-700 border border-purple-200 rounded-md text-xs font-medium transition-colors focus:ring-2 focus:ring-purple-500 focus:ring-offset-1"
                                                                        title="Export individual grade report"
                                                                    >
                                                                        <Download size={13} />
                                                                        Export
                                                                    </button>

                                                                    {/* Unlock - Secondary action for finalized grades */}
                                                                    {gradeStatus === 'final' && (
                                                                        <button
                                                                            onClick={() => unlockGrade(student.id)}
                                                                            className="flex items-center gap-1 px-2.5 py-1.5 bg-orange-50 hover:bg-orange-100 text-orange-700 border border-orange-200 rounded-md text-xs font-medium transition-colors focus:ring-2 focus:ring-orange-500 focus:ring-offset-1"
                                                                            title="Unlock finalized grade for re-grading"
                                                                        >
                                                                            <Unlock size={13} />
                                                                            Unlock
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            {/* Import Summary - Metadata about the imported class */}
                            <div className="bg-gradient-to-br from-gray-50 to-slate-50 border border-gray-200 rounded-xl p-5 shadow-sm">
                                <div className="flex items-center gap-2 mb-4">
                                    <FileSpreadsheet size={18} className="text-gray-600" />
                                    <h3 className="text-base font-semibold text-gray-800">Import Summary</h3>
                                </div>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    <div className="space-y-1">
                                        <div className="text-xs text-gray-500 font-medium uppercase tracking-wide">File</div>
                                        <div className="text-sm font-semibold text-gray-900 truncate" title={classList.fileName}>
                                            {classList.fileName}
                                        </div>
                                    </div>
                                    <div className="space-y-1">
                                        <div className="text-xs text-gray-500 font-medium uppercase tracking-wide">Import Date</div>
                                        <div className="text-sm font-semibold text-gray-900">
                                            {new Date(classList.importTime).toLocaleDateString()}
                                        </div>
                                    </div>
                                    <div className="space-y-1">
                                        <div className="text-xs text-gray-500 font-medium uppercase tracking-wide">Data Quality</div>
                                        <div className="flex items-center gap-2">
                                            <div className="text-sm font-semibold text-gray-900">
                                                {classList.validation?.validationScore || 0}%
                                            </div>
                                            <div className="flex-1 bg-gray-200 rounded-full h-1.5 max-w-[60px]">
                                                <div
                                                    className="bg-green-500 h-1.5 rounded-full"
                                                    style={{ width: `${classList.validation?.validationScore || 0}%` }}
                                                ></div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="space-y-1">
                                        <div className="text-xs text-gray-500 font-medium uppercase tracking-wide">Total Students</div>
                                        <div className="text-sm font-semibold text-gray-900">
                                            {classList.students.length}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
        </>
    );
};

export default ClassListManager;
